import React from "react";
import {
    MaterialReactTable,
    type MaterialReactTableProps,
} from "material-react-table";
import { SxProps } from "@mui/material";
import { Loading } from "components/loading";
import { COLORS } from "components/consts";

type CustomMaterialTableProps<T extends Record<string, any>> = {
    /** Override container styles (merged with base). */
    containerSx?: SxProps;
    /** Override header cell styles (merged with base). */
    headCellSx?: SxProps;
    /** Override body cell styles (merged with base). */
    bodyCellSx?: SxProps;
    /** Override header row styles (merged with base). */
    headRowSx?: SxProps;
    /** Override body row styles (merged with base). */
    bodyRowSx?: SxProps;
    /** Override top toolbar styles (merged with default collapsed toolbar). */
    topToolbarSx?: SxProps;
    /**
     * When true and data is empty, render <Loading /> instead of the table.
     * Use for initial load; when false, table is always rendered (e.g. with MRT loading overlay).
     */
    showLoadingWhenNoData?: boolean;
    enableTopToolbar?: boolean;
    enableRowVirtualization?: boolean;
    /** When true, rows show cursor pointer (e.g. when using onClick on rows). */
    rowClickable?: boolean;
    /** Custom message shown when no data is available (defaults to MRT's "No records to display."). */
    customPlaceholder?: string;
} & MaterialReactTableProps<T>;

/**
 * Merges extra sx into props, supporting both object and function forms (e.g. muiTableBodyRowProps).
 */
/** Base styles apply first; props (or function result) sx override. */
const resolveProps = (props: any, baseSx: SxProps): any => {
    if (typeof props === "function") {
        return (args: any) => {
            const result = props(args);
            return {
                ...result,
                sx: {
                    ...baseSx,
                    ...(result?.sx || {}),
                },
            };
        };
    }
    return {
        ...props,
        sx: {
            ...baseSx,
            ...(props?.sx || {}),
        },
    };
};

const baseContainerSx: SxProps = {
    border: "1px solid #D9D9D9",
    borderRadius: "10px",
    width: "100%",
    "& thead": {
        position: "sticky",
        top: 0,
        zIndex: 2,
    },
    "& tbody tr td:first-of-type": {
        paddingLeft: "20px",
    },
};

const defaultTopToolbarSx: SxProps = {
    height: "1px",
    minHeight: "1px",
    backgroundColor: "transparent",
    "& .MuiIconButton-root": {
        display: "none",
    },
};

const baseHeadCellSx: SxProps = {
    color: COLORS.Mainblue,
    lineHeight: "20px",
    padding: "8px 16px",
    textAlign: "left",
    "& .MuiBadge-root": {
        display: "none",
    },
    "&:first-of-type > div": {
        justifyContent: "flex-start",
    },
};

const baseHeadRowSx: SxProps = {
    height: "45px",
    backgroundColor: "#F8FAFC",
};

/** When virtualization + column filters are on, use these head row styles so the filter row lays out correctly. */
const headRowSxWhenVirtualizationAndFilters: SxProps = {
    "& > th:first-of-type": { paddingLeft: "20px" },
    "& > th": {
        paddingTop: 1,
        paddingBottom: 1,
        paddingLeft: "12px",
    },
    height: "auto",
    backgroundColor: "#F8FAFC",
};

const baseBodyCellSx: SxProps = {
    padding: "8px 16px",
    textAlign: "left",
};

const getBaseBodyRowSx = (rowClickable?: boolean): SxProps => ({
    minHeight: "45px",
    height: "fit-content",
    backgroundColor: "white",
    minWidth: "100%",
    width: "100%",
    maxWidth: "100%",
    ...(rowClickable ? { cursor: "pointer" } : {}),
});

export const CustomMaterialTable = <T extends Record<string, any>>({
    containerSx,
    headCellSx,
    bodyCellSx,
    headRowSx,
    bodyRowSx,
    topToolbarSx,
    showLoadingWhenNoData = false,
    muiTableContainerProps,
    muiTableHeadCellProps,
    muiTableBodyCellProps,
    muiTableHeadRowProps,
    muiTableBodyRowProps,
    muiTablePaperProps,
    muiTopToolbarProps,
    muiBottomToolbarProps,
    enableTopToolbar = true,
    enableRowVirtualization = true,
    enableColumnFilters = true,
    rowClickable = false,
    data,
    customPlaceholder,
    ...rest
}: CustomMaterialTableProps<T>) => {
    const isEmpty = !data || (Array.isArray(data) && data.length === 0);
    if (showLoadingWhenNoData && isEmpty) {
        return <Loading />;
    }

    const mergedTopToolbarSx = {
        ...defaultTopToolbarSx,
        ...topToolbarSx,
    };

    const useHeadRowSxForFilters =
        enableRowVirtualization && enableColumnFilters !== false;
    const resolvedBaseHeadRowSx: SxProps = useHeadRowSxForFilters
        ? { ...baseHeadRowSx, ...headRowSxWhenVirtualizationAndFilters }
        : baseHeadRowSx;

    return (
        <MaterialReactTable
            {...rest}
            enableColumnFilters={enableColumnFilters}
            data={data ?? ([] as T[])}
            enableTopToolbar={enableTopToolbar}
            enableRowVirtualization={enableRowVirtualization}
            muiTableContainerProps={resolveProps(muiTableContainerProps, {
                ...baseContainerSx,
                ...containerSx,
            })}
            muiTableHeadCellProps={resolveProps(muiTableHeadCellProps, {
                ...baseHeadCellSx,
                ...headCellSx,
            })}
            muiTableBodyCellProps={resolveProps(muiTableBodyCellProps, {
                ...baseBodyCellSx,
                ...bodyCellSx,
            })}
            muiTableHeadRowProps={resolveProps(muiTableHeadRowProps, {
                ...resolvedBaseHeadRowSx,
                ...headRowSx,
            })}
            muiTableBodyRowProps={resolveProps(muiTableBodyRowProps, {
                ...getBaseBodyRowSx(rowClickable),
                ...bodyRowSx,
            } as SxProps)}
            muiTablePaperProps={resolveProps(muiTablePaperProps, {
                borderRadius: "10px",
                boxShadow: "none",
            })}
            muiTopToolbarProps={resolveProps(muiTopToolbarProps, mergedTopToolbarSx)}
            muiBottomToolbarProps={resolveProps(muiBottomToolbarProps, {
                display: "none",
            })}
            localization={{ noRecordsToDisplay: customPlaceholder }}
        />
    );
};
